import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Menu,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SaveIcon from "@mui/icons-material/Save";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./ProductTypeMasterManagement.css";
import api, { emailTemplateAPI, getApiBaseUrl } from "../services/api";
import ExcelJS from "exceljs";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const normalizeEmailTemplate = (item) => {
  // Helper to safely get string value
  const getString = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    return String(val);
  };

  // Helper to safely get boolean value
  const getBoolean = (val) => {
    if (val === true || val === "true" || val === 1 || val === "1") return true;
    if (val === false || val === "false" || val === 0 || val === "0") return false;
    return false;
  };

  return {
    templateCode: getString(item.templateCode ?? item.TemplateCode ?? item.TEMPLATECODE ?? ""),
    templateName: getString(item.templateName ?? item.TemplateName ?? item.TEMPLATENAME ?? ""),
    eventTypeName: getString(item.eventTypeName ?? item.EventTypeName ?? item.EVENTTYPENAME ?? ""),
    subject: getString(item.subject ?? item.Subject ?? item.SUBJECT ?? ""),
    bodyTemplate: getString(item.bodyTemplate ?? item.BodyTemplate ?? item.BODYTEMPLATE ?? ""),
    eventType: getString(item.eventType ?? item.EventType ?? item.EVENTTYPE ?? ""),
    isHTML: getBoolean(item.isHTML ?? item.IsHTML),
    isActive: getBoolean(item.isActive ?? item.IsActive),
    marketCode: getString(item.marketCode ?? item.MarketCode ?? item.MARKETCODE ?? "0000"),
    marketName: item.marketName ?? item.MarketName ?? item.MARKETNAME ?? null,
    catgCode: getString(item.catgCode ?? item.CatgCode ?? item.CATGCODE ?? "0000"),
    catgName: item.catgName ?? item.CatgName ?? item.CATGNAME ?? null,
    srTypeCode: item.srTypeCode ?? item.SRTypeCode ?? item.SRTYPECODE ?? null,
    srTypeName: item.srTypeName ?? item.SRTypeName ?? item.SRTYPENAME ?? null,
    createdBy: getString(item.createdBy ?? item.CreatedBy ?? item.CREATEDBY ?? ""),
    createdOn: item.createdOn ?? item.CreatedOn ?? null,
    modifiedBy: getString(item.modifiedBy ?? item.ModifiedBy ?? item.MODIFIEDBY ?? ""),
    modifiedOn: item.modifiedOn ?? item.ModifiedOn ?? null,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

/** DataTable column indexes: Event Name, Notification Type (eventType) */
const EMAIL_TABLE_LEFT_HEADER_COLS = [1, 3];

const applyEmailTableLeftHeaders = (api) => {
  if (!api) return;
  EMAIL_TABLE_LEFT_HEADER_COLS.forEach((colIdx) => {
    const header = api.column(colIdx).header();
    if (!header) return;
    const $th = $(header);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
  const $wrapper = $(api.table().node()).closest(".dataTables_wrapper, .dt-container");
  $wrapper.find(".dt-scroll-head thead th").each(function () {
    const colIdx = parseInt($(this).attr("data-dt-column"), 10);
    if (!EMAIL_TABLE_LEFT_HEADER_COLS.includes(colIdx)) return;
    const $th = $(this);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
};

const EmailTemplateManagement = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    eventType: "",
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState("");
  const [templateConfig, setTemplateConfig] = useState({
    notificationEventTypes: [],
    defaultRecipients: [],
    replyToEmail: "",
    ccEmails: [],
  });
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);
  const eventTypeSelectRef = useRef(null);
  const defaultRecipientsSelectRef = useRef(null);
  const isMountedRef = useRef(true);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    isMountedRef.current = true;
    fetchTemplates();
    
    const handleDocumentClick = (e) => {
      const $target = $(e.target);
      const $statusToggle = $target.closest(".status-toggle");
      if ($statusToggle.length && $(tableRef.current).find($statusToggle).length) {
        e.preventDefault();
        e.stopPropagation();
        const code = $statusToggle.attr("data-code") || $statusToggle.data("code");
        const currentActiveStr = $statusToggle.attr("data-active") || $statusToggle.data("active");
        const currentActive =
          currentActiveStr === true ||
          currentActiveStr === "true" ||
          currentActiveStr === 1 ||
          currentActiveStr === "1";
        const newActive = !currentActive;
        if (code) {
          handleToggleStatus(code, newActive);
        }
      }
    };
    
    $(document).on("click", ".status-toggle", handleDocumentClick);
    
    return () => {
      isMountedRef.current = false;
      $(document).off("click", ".status-toggle", handleDocumentClick);
    };
  }, []);


  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = {
        searchText: filters.search || undefined,
        status: filters.status === "all" ? undefined : filters.status,
        eventType: filters.eventType || undefined,
        pageNumber,
        pageSize,
      };

      const response = await emailTemplateAPI.getAll(params);
      console.log("Email Templates API Response:", response);

      if (response?.success && response.data) {
        // Handle both PascalCase (Templates) and camelCase (templates) response structures
        let rawTemplates = response.data.Templates || response.data.templates || [];
        if (!Array.isArray(rawTemplates)) {
          console.warn("Templates is not an array:", rawTemplates);
          rawTemplates = [];
        }
        
        console.log("Raw templates count:", rawTemplates.length);
        
        // Normalize property names to camelCase for DataTable
        const normalizedTemplates = rawTemplates.map(normalizeEmailTemplate);
        console.log("Normalized templates count:", normalizedTemplates.length);
        
        // Sort by createdOn in descending order (newest first)
        const sorted = normalizedTemplates.sort((a, b) => {
          const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
          const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
          return dateB - dateA; // Descending order
        });
        
        setTemplates(sorted);
        setTotalRecords(response.data.TotalRecords || response.data.totalRecords || sorted.length);
        initializeDataTable(sorted);
      } else {
        console.warn("API response not successful or missing data:", response);
        setTemplates([]);
        setTotalRecords(0);
        initializeDataTable([]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      let errorMessage = "Failed to load email templates";
      
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
        errorMessage = "Network Error: Unable to connect to the server. Please check if the API server is running.";
      } else if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || `Server Error: ${error.response.status} ${error.response.statusText}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        footer: error.config?.url ? `<small>URL: ${error.config.baseURL}${error.config.url}</small>` : undefined,
      });
      setTemplates([]);
      setTotalRecords(0);
      initializeDataTable([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeDataTable = (data) => {
    if (dataTableInstanceRef.current) {
      try {
        const $table = $(tableRef.current);
        $table.off("click", ".edit-template");
        $table.off("click", ".delete-template");
        $table.off("click", ".status-toggle");
        if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableRef.current)) {
          dataTableInstanceRef.current.destroy();
        }
      } catch (e) {
        console.warn("Error destroying DataTable:", e);
      }
      dataTableInstanceRef.current = null;
    }

    if (tableRef.current) {
      try {
        if (!$.fn.DataTable) {
          DataTable($);
        }

        dataTableInstanceRef.current = $(tableRef.current).DataTable({
          data: data,
          paging: true,
          searching: true,
          info: true,
          lengthChange: true,
          lengthMenu: [5, 10, 25, 50, 100],
          dom: "lfrtip",
          ordering: true,
          destroy: true,
          scrollX: true,
          autoWidth: false,
          columns: [
            {
              title: "Template Code",
              data: "templateCode",
              visible: false,
              render: (d) => {
                if (d === null || d === undefined || d === false) return "-";
                const str = String(d).trim();
                return str || "-";
              },
            },
            {
              title: "Event Name",
              data: "eventTypeName",
              className: "text-start",
              render: (d) => d || "-",
            },
            {
              title: "Subject",
              data: "subject",
              visible: false,
              render: (d) => d || "-",
            },
            {
              title: "Notification Type",
              data: "eventType",
              className: "text-start",
              render: (d) => d || "-",
            },
            {
              title: "Is HTML",
              data: "isHTML",
              visible: false,
              className: "text-center",
              render: (d) => {
                const isHTML = d === true || d === "true" || d === 1 || d === "1";
                return isHTML ? "Yes" : "No";
              },
            },
            {
              title: "Created On",
              data: "createdOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Modified On",
              data: "modifiedOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Status",
              data: null,
              orderable: false,
              render: (d, type, row) => {
                const templateCode = row?.templateCode || "";
                const isActive = row?.isActive === true || row?.isActive === 1;
                const toggleClass = isActive ? "active" : "inactive";
                const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
                return `<div class="d-flex justify-content-center">
                  <button class="btn btn-sm status-toggle ${toggleClass}" 
                    data-code="${templateCode}" 
                    data-active="${isActive}"
                    type="button"
                    title="${isActive ? "Click to deactivate" : "Click to activate"}"
                    style="background: transparent !important; 
                           border: none !important; 
                           padding: 0 !important;
                           font-size: 24px !important;
                           color: ${isActive ? "#4caf50" : "#9e9e9e"} !important;
                           cursor: pointer !important;">
                    <i class="bi ${toggleIcon}"></i>
                  </button>
                </div>`;
              },
            },
            {
              title: "Action",
              data: null,
              orderable: false,
              searchable: false,
              render: (d, type, row) => {
                const templateCode = row?.templateCode || "";
                return `<div class="d-flex justify-content-center gap-2">
                  <button class="btn btn-sm edit-template" data-code="${templateCode}" title="Edit Template"
                    style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-pencil-fill"></i>
                  </button>
                </div>`;
              },
            },
          ],
          order: [[6, "desc"]], // Order by Modified On descending
          language: {
            emptyTable: "No email templates found",
          },
          initComplete: function () {
            applyEmailTableLeftHeaders(this.api());
            try {
              const wrapper = $(tableRef.current).closest(
                ".dataTables_wrapper, .dt-container"
              );
              if (wrapper.length === 0) return;

              const lengthEl = wrapper.find(
                ".dt-length, .dataTables_length"
              );
              const searchEl = wrapper.find(
                ".dt-search, .dataTables_filter"
              );

              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $(
                    "<div class='dtFltrTop d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info, .dataTables_info");
              const pagingEl = wrapper.find(
                ".dt-paging, .dataTables_paginate"
              );
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            } catch (err) {
              console.warn("DataTable layout wrap failed:", err);
            }
          },
          headerCallback: function () {
            applyEmailTableLeftHeaders(this.api());
          },
          drawCallback: function () {
            applyEmailTableLeftHeaders(this.api());
          },
        });

        const $table = $(tableRef.current);
        $table
          .off("click", ".edit-template")
          .on("click", ".edit-template", function () {
            const code = $(this).data("code");
            navigate(`/email-templates/edit/${code}`);
          });

        $table
          .off("click", ".delete-template")
          .on("click", ".delete-template", function () {
            const code = $(this).data("code");
            handleDelete(code);
          });

        $table
          .off("click", ".settings-template")
          .on("click", ".settings-template", function () {
            const code = $(this).data("code");
            handleOpenConfigModal(code);
          });
      } catch (error) {
        console.error("Error initializing DataTable:", error);
      }
    }
  };

  const handleToggleStatus = async (templateCode, newStatus) => {
    const actionText = newStatus ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this email template?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newStatus ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newStatus ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await emailTemplateAPI.updateStatus(templateCode, newStatus);
        if (response && response.success) {
          Swal.fire("Success!", `Email template has been ${actionText}d.`, "success");
          fetchTemplates();
        } else {
          Swal.fire("Error", response?.message || `Failed to ${actionText} email template`, "error");
        }
      } catch (error) {
        console.error(`Error ${actionText}ing email template:`, error);
        Swal.fire("Error", `Failed to ${actionText} email template. Please try again.`, "error");
      }
    }
  };

  const handleDelete = async (templateCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the email template. This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await emailTemplateAPI.delete(templateCode);
        if (response?.success) {
          Swal.fire("Deleted!", "The email template has been deleted.", "success");
          fetchTemplates();
        } else {
          Swal.fire("Error", response?.message || "Failed to delete email template", "error");
        }
      } catch (error) {
        console.error("Error deleting email template:", error);
        Swal.fire("Error", "Failed to delete email template", "error");
      }
    }
  };

  const handleOpenConfigModal = async (templateCode) => {
    setSelectedTemplateCode(templateCode);
    try {
      // Load existing configuration
      const response = await emailTemplateAPI.getConfiguration(templateCode);
      if (response?.success && response.data) {
        setTemplateConfig({
          notificationEventTypes: response.data.notificationEventTypes || [],
          defaultRecipients: response.data.defaultRecipients || [],
          replyToEmail: response.data.replyToEmail || "",
          ccEmails: response.data.ccEmails || [],
        });
      } else {
        // Initialize with empty values
        setTemplateConfig({
          notificationEventTypes: [],
          defaultRecipients: [],
          replyToEmail: "",
          ccEmails: [],
        });
      }
      setConfigModalOpen(true);
    } catch (error) {
      console.error("Error loading template configuration:", error);
      // Initialize with empty values on error
      setTemplateConfig({
        notificationEventTypes: [],
        defaultRecipients: [],
        replyToEmail: "",
        ccEmails: [],
      });
      setConfigModalOpen(true);
    }
  };

  const handleCloseConfigModal = () => {
    // Destroy Select2 before closing
    if (defaultRecipientsSelectRef.current) {
      const $select = $(defaultRecipientsSelectRef.current);
      if ($select.data("select2")) {
        try {
          $select.off("change");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }
    }
    setConfigModalOpen(false);
    setSelectedTemplateCode("");
    setTemplateConfig({
      notificationEventTypes: [],
      defaultRecipients: [],
      replyToEmail: "",
      ccEmails: [],
    });
  };

  // Initialize Select2 for Default Recipients when modal opens
  useEffect(() => {
    if (!configModalOpen) {
      return;
    }

    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 10;

    const initializeSelect2 = () => {
      if (!isMounted) return;

      if (!defaultRecipientsSelectRef.current) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeSelect2, 100);
        } else {
          console.warn("Default recipients select ref is null after retries");
        }
        return;
      }

      const selectElement = defaultRecipientsSelectRef.current;
      const $select = $(selectElement);
      
      if (!$select.length) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeSelect2, 100);
        } else {
          console.warn("jQuery selector returned empty for select element");
        }
        return;
      }

      // Check if element is visible
      if (!$select.is(":visible")) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeSelect2, 100);
        } else {
          console.warn("Select element is not visible after retries");
        }
        return;
      }

      console.log("Initializing Select2 for Default Recipients", selectElement);

      // Destroy existing Select2 if any
      if ($select.data("select2")) {
        try {
          $select.off("change.select2");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }

      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem("token");

      // Initialize Select2 with AJAX using custom transport
      try {
        $select.select2({
          width: "100%",
          placeholder: "Type to search recipients (min 2 characters)...",
          allowClear: true,
          multiple: true,
          ajax: {
            transport: function (params, success, failure) {
              const searchTerm = params.data?.term || params.term || "";
              console.log("Select2 AJAX request - searching for:", searchTerm);

              const baseUrl = getApiBaseUrl();
              const token = localStorage.getItem("token");

              // Use jQuery ajax directly for better compatibility with Select2
              const request = $.ajax({
                url: `${baseUrl}/api/Users/search-by-email`,
                type: "GET",
                dataType: "json",
                data: {
                  searchText: searchTerm,
                },
                beforeSend: function (xhr) {
                  xhr.setRequestHeader("Content-Type", "application/json");
                  if (token) {
                    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
                  }
                },
                success: function (data, textStatus, jqXHR) {
                  console.log("Select2 AJAX success - data:", data);
                  console.log("Response structure:", JSON.stringify(data, null, 2));
                  if (success && typeof success === "function") {
                    success(data);
                  }
                },
                error: function (jqXHR, textStatus, errorThrown) {
                  console.error("Select2 AJAX error:", {
                    status: jqXHR.status,
                    statusText: textStatus,
                    error: errorThrown,
                    response: jqXHR.responseText,
                    url: jqXHR.responseURL,
                  });
                  if (failure && typeof failure === "function") {
                    failure(jqXHR, textStatus, errorThrown);
                  }
                },
              });

              return request;
            },
            delay: 300,
            processResults: function (data, params) {
              console.log("Select2 processResults - received data:", data);
              console.log("Data type:", typeof data);
              console.log("Data structure:", JSON.stringify(data, null, 2));
              
              // Handle the ApiResponse structure
              if (data) {
                // Check if data has success and data properties (ApiResponse format)
                if (data.success !== undefined && data.data !== undefined) {
                  if (data.success && Array.isArray(data.data)) {
                    const results = data.data.map((user) => {
                      const email = user.email || user.Email || "";
                      const displayName = user.displayName || user.DisplayName || "";
                      return {
                        id: email,
                        text: displayName ? `${email} (${displayName})` : email,
                      };
                    });
                    console.log("Select2 processResults - processed results:", results);
                    return {
                      results: results,
                    };
                  }
                }
                // Check if data is directly an array
                else if (Array.isArray(data)) {
                  const results = data.map((user) => {
                    const email = user.email || user.Email || "";
                    const displayName = user.displayName || user.DisplayName || "";
                    return {
                      id: email,
                      text: displayName ? `${email} (${displayName})` : email,
                    };
                  });
                  console.log("Select2 processResults - processed results (direct array):", results);
                  return {
                    results: results,
                  };
                }
              }
              
              console.warn("Select2 processResults - no valid data structure:", data);
              return { results: [] };
            },
            cache: true,
          },
          minimumInputLength: 2,
          language: {
            inputTooShort: function () {
              return "Please enter at least 2 characters to search";
            },
            searching: function () {
              return "Searching...";
            },
            noResults: function () {
              return "No users found";
            },
            loadingMore: function () {
              return "Loading more results...";
            },
          },
        });

        console.log("Select2 initialized successfully");
        console.log("Select2 instance:", $select.data("select2"));

        // Test if Select2 is working by checking if it's initialized
        if ($select.data("select2")) {
          console.log("Select2 is properly initialized");
        } else {
          console.error("Select2 failed to initialize!");
        }

        // Set existing values after Select2 is ready
        setTimeout(() => {
          if (templateConfig.defaultRecipients.length > 0) {
            const emails = templateConfig.defaultRecipients.filter((e) => e && e.trim());
            if (emails.length > 0) {
              console.log("Setting existing recipients:", emails);
              $select.val(emails).trigger("change");
            }
          }
        }, 300);

        // Handle change event
        $select.on("change.select2", function (e) {
          const selectedEmails = Array.from($select.val() || []);
          console.log("Recipients changed:", selectedEmails);
          setTemplateConfig((prev) => ({
            ...prev,
            defaultRecipients: selectedEmails,
          }));
        });
      } catch (error) {
        console.error("Error initializing Select2:", error);
      }
    };

    // Start initialization after a short delay
    const timer = setTimeout(initializeSelect2, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (defaultRecipientsSelectRef.current) {
        const $select = $(defaultRecipientsSelectRef.current);
        if ($select.data("select2")) {
          try {
            $select.off("change.select2");
            $select.select2("destroy");
          } catch (e) {
            console.warn("Error cleaning up Select2:", e);
          }
        }
      }
    };
  }, [configModalOpen]);


  const handleSaveConfiguration = async () => {
    if (templateConfig.notificationEventTypes.length === 0) {
      Swal.fire("Error", "At least one Notification Event Type is required", "error");
      return;
    }

    const configToSave = {
      notificationEventTypes: templateConfig.notificationEventTypes,
      defaultRecipients: templateConfig.defaultRecipients,
      replyToEmail: templateConfig.replyToEmail.trim(),
      ccEmails: templateConfig.ccEmails,
    };

    try {
      const response = await emailTemplateAPI.saveConfiguration(selectedTemplateCode, configToSave);
      if (response?.success) {
        Swal.fire("Success!", "Template configuration saved successfully", "success");
        handleCloseConfigModal();
      } else {
        Swal.fire("Error", response?.message || "Failed to save configuration", "error");
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      Swal.fire("Error", "Failed to save configuration. Please try again.", "error");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchTemplates();
  };

  const handleReset = () => {
    setFilters({
      search: "",
      status: "all",
      eventType: "",
    });
    setTimeout(() => {
      fetchTemplates();
    }, 100);
  };

  useEffect(() => {
    fetchTemplates();
  }, [filters.status, filters.eventType]);

  // Initialize Select2 dropdowns
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);
      if (!$select.data("select2")) {
        $select.select2({
          width: "100%",
          placeholder: "Select Status",
          allowClear: false,
        });
        $select.on("change", function (e) {
          setFilters((prev) => ({ ...prev, status: e.target.value }));
        });
        $select.val(filters.status).trigger("change");
      }
    }
  }, [filters.status]);

  useEffect(() => {
    if (eventTypeSelectRef.current) {
      const $select = $(eventTypeSelectRef.current);
      const isSelect2Initialized = $select.data("select2");
      
      if (!isSelect2Initialized) {
        $select.select2({
          width: "100%",
          placeholder: "Select Notification Type",
          allowClear: true,
        });
        $select.on("change", function (e) {
          setFilters((prev) => ({ ...prev, eventType: e.target.value || "" }));
        });
        if (filters.eventType) {
          $select.val(filters.eventType).trigger("change");
        }
      } else {
        // Update value if it changed
        if ($select.val() !== filters.eventType) {
          $select.val(filters.eventType || "").trigger("change");
        }
      }
    }
  }, [filters.eventType]);


  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToExcel = async () => {

    // Export what the grid is actually showing: when a search is active

    // only the matching rows are written out, otherwise the full data set.

    const exportRows = getDataTableExportRows(dataTableInstanceRef, templates);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Email Templates");

      worksheet.mergeCells("A1:H1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "Email Template Management Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Template Code", "Event Name", "Subject", "Event Type", "Is HTML", "Created On", "Modified On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      exportRows.forEach((template, index) => {
        const row = worksheet.addRow([
          index + 1,
          template.templateCode || "",
          template.eventTypeName || template.templateName || "",
          template.subject || "",
          template.eventType || "",
          template.isHTML ? "Yes" : "No",
          formatDateTime(template.createdOn),
          formatDateTime(template.modifiedOn),
          template.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `Email_Templates_${new Date().toISOString().split("T")[0]}.xlsx`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-user-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Email Configuration
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage email templates for notifications and communications.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => { handleExportMenuClose(); exportToExcel(); }}>
              Excel
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/email-templates/add")}
            sx={{
              background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
              border: "none",
              color: "white",
              fontWeight: 600,
              fontSize: "11px",
              boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
              borderRadius: "4px",
              textTransform: "uppercase",
              padding: "8px 24px",
              "&:hover": {
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter: Notification Type</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            {/* Search option hidden */}
            <div className="col-12 col-md-6 col-lg-3 srchDvwrpr" style={{ display: "none" }}>
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search templates..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  style={{
                    paddingLeft: "40px",
                    height: "42px",
                  }}
                />
                <SearchIcon
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Notification Type
              </label>
              <select
                ref={eventTypeSelectRef}
                className="form-select form-control-light filter-select text-start"
                value={filters.eventType}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="">All Notification Types</option>
                <option value="Email">Email</option>
                <option value="Notification">Notification</option>
              </select>
            </div>

{/*             <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div> */}

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={handleSearch}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={handleReset}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {loading && templates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading email templates...</Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No email templates found. Click "Add Email Template" to create a new template.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>

      {/* Template Configuration Modal */}
      <Dialog
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "8px",
            backgroundColor: "#f5f5f5",
            margin: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              color: "#333",
              fontSize: "18px",
            }}
          >
            Template Configuration
          </Typography>
          <IconButton
            onClick={handleCloseConfigModal}
            sx={{
              color: "#666",
              padding: "4px",
              "&:hover": {
                backgroundColor: "#e0e0e0",
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
            }}
          >
            {/* Notification Event Types */}
            <Box sx={{ gridColumn: "span 3" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                Notification Event Types{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter event types (one per line or comma-separated)"
                value={templateConfig.notificationEventTypes.join("\n")}
                onChange={(e) => {
                  const values = e.target.value
                    .split(/[\n,]/)
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);
                  setTemplateConfig((prev) => ({
                    ...prev,
                    notificationEventTypes: values,
                  }));
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor: "#adb5bd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                }}
              />
            </Box>

            {/* Default Recipients */}
            <Box sx={{ gridColumn: "span 3" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                Default Recipients
              </Typography>
              <select
                ref={defaultRecipientsSelectRef}
                className="form-select form-control-light filter-select text-start"
                multiple
                id="defaultRecipientsSelect"
                style={{
                  width: "100%",
                  height: "auto",
                  minHeight: "80px",
                  textAlign: "left",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </Box>

            {/* Reply To Email */}
            <Box sx={{ gridColumn: "span 3" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                Reply To Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="email@example.com"
                value={templateConfig.replyToEmail}
                onChange={(e) =>
                  setTemplateConfig((prev) => ({
                    ...prev,
                    replyToEmail: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor: "#adb5bd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                }}
              />
            </Box>

            {/* CC Emails */}
            <Box sx={{ gridColumn: "span 3" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                CC Emails
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter email addresses (one per line or comma-separated)"
                value={templateConfig.ccEmails.join("\n")}
                onChange={(e) => {
                  const values = e.target.value
                    .split(/[\n,]/)
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);
                  setTemplateConfig((prev) => ({
                    ...prev,
                    ccEmails: values,
                  }));
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor: "#adb5bd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#f5f5f5",
            borderRadius: "0 0 8px 8px",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleCloseConfigModal}
            sx={{
              background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
              color: "white",
              fontWeight: 700,
              textTransform: "uppercase",
              padding: "10px 24px",
              borderRadius: "4px",
              fontSize: "14px",
              boxShadow: "0 2px 4px rgba(220, 53, 69, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #c82333 0%, #bd2130 100%)",
                boxShadow: "0 4px 8px rgba(220, 53, 69, 0.4)",
              },
            }}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfiguration}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 700,
              textTransform: "uppercase",
              padding: "10px 24px",
              borderRadius: "4px",
              fontSize: "14px",
              boxShadow: "0 2px 4px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 4px 8px rgba(102, 126, 234, 0.4)",
              },
            }}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateManagement;
